// @ts-ignore -- there are no types for devlop and the bundler doesn't like it
import { ok as assert } from "devlop";
import { type List, type ListItem, type Nodes, type Parents } from "mdast";
import { type Processor } from "unified";
import { CONTINUE, SKIP, visit } from "unist-util-visit";

declare module "mdast" {
	interface ListItemData {
		depth?: number;
		bullet?: string;
		originalIndent?: number;
	}
}

export function remarkArbitraryListDepth(this: Processor) {
	const data = this.data();

	const micromarkExtensions = data.micromarkExtensions || (data.micromarkExtensions = []);
	const fromMarkdownExtensions =
		data.fromMarkdownExtensions || (data.fromMarkdownExtensions = []);
	const toMarkdownExtensions = data.toMarkdownExtensions || (data.toMarkdownExtensions = []);

	micromarkExtensions.push({
		disable: {
			null: [
				// We disable these because they compete with our
				// arbitrary depth lists
				"codeIndented",
			],
		},
	});

	fromMarkdownExtensions.push({
		transforms: [
			function (tree) {
				visit(tree, "list", function (topList, index, parent) {
					// We don't allow stuff like block quotes nested in lists.
					const isSimpleList =
						topList.children.length === 1 &&
						topList.children[0]?.type === "listItem" &&
						topList.children[0].children.length === 1 &&
						topList.children[0].children[0]?.type === "paragraph";

					if (!parent || index === undefined || isSimpleList) {
						return CONTINUE;
					}

					const items: List[] = [];
					visit(topList, function (desc, descIndex, descParent) {
						if (descIndex === undefined || !descParent) {
							return CONTINUE;
						}

						if (descParent.type === "listItem" && desc.type === "paragraph") {
							items.push({
								type: "list",
								children: [
									{
										...descParent,
										type: "listItem",
										children: [desc],
									},
								],
							});
						}

						return CONTINUE;
					});

					parent.children.splice(index, 1);
					parent.children.splice(index, 0, ...items);

					return SKIP;
				});
			},
		],
		exit: {
			listItem(token) {
				// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
				const node = this.stack[this.stack.length - 1]! as ListItem;
				assert(node.type === "listItem");
				assert(node.position);
				node.data ??= {};
				// Store the original indentation amount (number of spaces before the list marker)
				const originalIndent = (node.position?.start.column ?? 1) - 1;
				node.data.originalIndent = originalIndent;
				// Keep the depth calculation for backward compatibility, but normalize to 4-space increments
				node.data.depth = Math.ceil(originalIndent / 4);
				this.exit(token);
			},
			listItemMarker(token) {
				const parent = this.stack[this.stack.length - 1];
				if (parent?.type !== "listItem") {
					return;
				}

				if (parent.data?.["bullet"]) {
					return;
				}
				parent.data ??= {};
				parent.data["bullet"] = this.sliceSerialize(token);
			},
			listItemValue(token) {
				const parent = this.stack[this.stack.length - 1];
				if (parent?.type !== "listItem") {
					return;
				}

				assert(!Number.isNaN(parseInt(this.sliceSerialize(token), 10)));

				parent.data ??= {};
				parent.data["bullet"] = "#";
			},
		},
	});

	function getBullet(node: ListItem, parent: Parents | undefined) {
		if (node.data?.bullet !== "#") {
			return node.data?.bullet ?? "-";
		}
		if (parent?.type !== "root") {
			return "-";
		}
		const index = parent.children.indexOf(node);

		let numSiblings = 0;
		for (let i = index; i >= 0; i--) {
			const prevNode = parent.children[i - 1];
			if (
				prevNode?.type !== "listItem" ||
				prevNode.data?.depth !== node.data?.depth ||
				prevNode.data?.bullet !== node.data?.bullet
			) {
				break;
			}
			numSiblings++;
		}

		return `${numSiblings + 1}.`;
	}

	toMarkdownExtensions.push({
		unsafe: [{ atBreak: true, character: "-", after: "[:|-]" }],
		handlers: {
			listItem(node: ListItem, parent, state, info) {
				const head = node.children[0];
				const checkable =
					typeof node.checked === "boolean" && head && head.type === "paragraph";
				const checkbox = "[" + (node.checked ? "x" : " ") + "] ";

				const tracker = state.createTracker(info);

				if (checkable) {
					tracker.move(checkbox);
				}

				const bullet = getBullet(node, parent);

				// Use the original indentation amount if available, otherwise fall back to depth * 4
				const indent = node.data?.originalIndent ?? (node.data?.depth ?? 0) * 4;
				const size = bullet.length + 1;

				tracker.move(" ".repeat(indent) + bullet + " ".repeat(size - bullet.length));
				tracker.shift(indent + size);
				const exit = state.enter("listItem");
				let value = state.indentLines(
					state.containerFlow(node, tracker.current()),
					(line, index, blank) => {
						if (index) {
							return (blank ? "" : " ".repeat(size)) + line;
						}

						return (
							(blank
								? bullet
								: " ".repeat(indent) + bullet + " ".repeat(size - bullet.length)) +
							line
						);
					}
				);
				exit();

				if (checkable) {
					value = value.replace(/^ *(?:[*+-]|\d+\.)([\r\n]| {1,3})/, ($0) => {
						return $0 + checkbox;
					});
				}

				return value;
			},
		},
		join: [
			(left: Nodes, right: Nodes) => {
				if (left.type === "listItem" && right.type === "listItem") {
					return 0;
				}
			},
		],
	});
}
